import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ProfileDetails } from "../../../model/settings/SettingsPersonalInfoModel";
import SettingsPersonalProfileProvider from "./SettingsPersonalProfileProvider";
import { RootState } from "../../../libraries/state_reducers/store";
import { useSelector } from "react-redux";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import { AlertMesage } from "../../../widget/common/ui/Alerts";
import Loader from "../../../widget/common/ui/Loader";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import ShowToast from "../../../widget/common/ui/Toast";
import { appRoutes } from "../../../routes/routes";
import { textCase } from "../../../service/ui/UI";
import SessionStorage from "../../../service/storage/SessionStorage";
import StorageKeys from "../../../const/StorageKeys";
import ImagePickerPreviewProvider from "../../widget/ImagePickerPreviewProvider";
import { phoneMobileDefault } from "../../../const/MobileConstant";

type SettingsPersonalProfileTypes = {
  profileDetail: ProfileDetails | null;
  firstNameField: string;
  lastNameField: string;
  emailField: string;
  phoneField: string;
  phoneCodeField: string;
  phoneCodeIdField: string;

  countryId: string;
  countryName: string;
  stateId: string;
  stateName: string;
  cityId: string;
  cityName: string;
  areaId: string;
  areaName: string;

  allowEdit: boolean;

  getProfileInfo: () => void;
  submitDetail: () => void;

  profileErrors: ProfileErrorType;
  profileValidate: boolean;
  handleOnChange: (name: string, value: string) => void;

  updateFileString: (
    fileString: string | null,
    fileDetail: File | null
  ) => void;
};

type ProfileErrorType = {
  firstNameField: boolean;
  lastNameField: boolean;
};

const SettingsPersonalProfileContext = createContext<
  SettingsPersonalProfileTypes | undefined
>(undefined);

export const SettingsPersonalProfileContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const countryCodeDetail = useSelector(
    (state: RootState) => state.countryCode.countryCodeDetail
  );
  const geoDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.geo
  );

  const geoRefreshDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.timestamp
  );

  const profileDetailRef = useRef<ProfileDetails | null>(null);

  const profileErrors: ProfileErrorType = {
    firstNameField: false,
    lastNameField: false,
  };
  /* const [profileErrors, setProfileErrors] = useState<ProfileErrorType>({
    firstNameField: false,
    lastNameField: false,
  }); */

  const [profileDetail, setProfileDetail] = useState<ProfileDetails | null>(
    null
  );
  const [fileDetail, setFileDetail] = useState<File | null>(null);
  const [allowEdit, setAllowEdit] = useState<boolean>(false);
  const [profileValidate, setProfileValidate] = useState<boolean>(false);

  const [firstNameField, setFirstNameField] = useState<string>("");
  const [lastNameField, setLastNameField] = useState<string>("");
  const [emailField, setEmailField] = useState<string>("");
  const [phoneField, setPhoneField] = useState<string>("");
  const [phoneCodeField, setPhoneCodeField] = useState<string>(
    phoneMobileDefault.phone_code
  );
  const [phoneCodeIdField, setPhoneCodeIdField] = useState<string>(
    phoneMobileDefault.phone_code_id
  );
  //const [cityField, setCityField] = useState<string>("");

  const [countryId, setCountryIdField] = useState<string>("0");
  const [countryName, setCountryNameField] = useState<string>("");
  const [stateId, setStateIdField] = useState<string>("0");
  const [stateName, setStateNameField] = useState<string>("");
  const [cityId, setCityIdField] = useState<string>("0");
  const [cityName, setCityNameField] = useState<string>("");
  const [areaId, setAreaIdField] = useState<string>("0");
  const [areaName, setAreaNameField] = useState<string>("");

  const updateFileString = (
    fileString: string | null,
    fileDetail: File | null
  ) => {
    setFileDetail(fileDetail);
  };

  const resetForm = () => {
    setFileDetail(null);
    setAllowEdit(false);
    setProfileValidate(false);
  };

  const getProfileInfo = async () => {
    resetForm();

    const settingspersonalprofilePersonalInfoModel =
      await SettingsPersonalProfileProvider.getProfileAPI();
    if (settingspersonalprofilePersonalInfoModel !== null) {
      const profileDetailResult: ProfileDetails | null =
        settingspersonalprofilePersonalInfoModel?.result?.profile_details!;
      profileDetailRef.current = profileDetailResult;

      setProfileDetail(profileDetailResult);

      setFirstNameField(textCase(profileDetailResult?.first_name!));
      setLastNameField(textCase(profileDetailResult?.last_name!));
      setEmailField(textCase(profileDetailResult?.email!));
      setPhoneField(textCase(profileDetailResult?.mobile_no!));
      setPhoneCodeField(textCase(profileDetailResult?.mobile_code!));
      setPhoneCodeIdField(textCase(profileDetailResult?.mobile_code_id!));

      setCountryIdField(profileDetailResult?.country_id!);
      setCountryNameField(textCase(profileDetailResult?.country_name!));
      setStateIdField(profileDetailResult?.state_id!);
      setStateNameField(textCase(profileDetailResult?.state_name!));
      setCityIdField(profileDetailResult?.city_id!);
      setCityNameField(textCase(profileDetailResult?.city_name!));
      setAreaIdField(profileDetailResult?.area_id!);
      setAreaNameField(textCase(profileDetailResult?.area_name!));

      SessionStorage.saveValue(
        StorageKeys.settings_personal_info_email,
        profileDetailResult?.email!
      );

      SessionStorage.saveValue(
        StorageKeys.settings_personal_info_phone_number,
        JSON.stringify({
          phone_code: profileDetailResult?.mobile_code!,
          phone_code_id: profileDetailResult?.mobile_code_id!,
          phone_number: profileDetailResult?.mobile_no!,
        })
      );
    }
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();

    if (fieldName === `first_name`) {
      setFirstNameField(fieldValue);
    } else if (fieldName === `last_name`) {
      setLastNameField(fieldValue);
    } else if (fieldName === `phone_number_optional`) {
      setPhoneField(fieldValue);
    }
  };

  const validateErrors = (): boolean => {
    let errorCounter = 0;

    /* let profileErrorsTemp: ProfileErrorType = {
      firstNameField: false,
      lastNameField: false,
    }; */

    if (UtilityHelper.isEmpty(firstNameField)) {
      errorCounter++;
      //profileErrorsTemp = { ...profileErrorsTemp, firstNameField: true };
    }

    if (UtilityHelper.isEmpty(lastNameField)) {
      errorCounter++;
      //profileErrorsTemp = { ...profileErrorsTemp, lastNameField: true };
    }

    setProfileValidate(errorCounter === 0 ? true : false);
    return errorCounter === 0 ? true : false;
  };

  const submitDetail = async () => {
    if (allowEdit) {
      if (validateErrors()) {
        const profileDet = profileDetailRef.current;
        let countChanges = 0;

        if (
          firstNameField.toLowerCase() !== profileDet?.first_name!.toLowerCase()
        ) {
          countChanges++;
        }
        if (
          lastNameField.toLowerCase() !== profileDet?.last_name!.toLowerCase()
        ) {
          countChanges++;
        }
        if (phoneField.toLowerCase() !== profileDet?.mobile_no!.toLowerCase()) {
          countChanges++;
        }
        if (
          phoneCodeIdField.toLowerCase() !==
          profileDet?.mobile_code_id!.toLowerCase()
        ) {
          countChanges++;
        }
        if (cityId.toLowerCase() !== profileDet?.city_id!.toLowerCase()) {
          countChanges++;
        }

        if (countChanges > 0) {
          Loader.modal();
          const statusResponse =
            await SettingsPersonalProfileProvider.updateProfileAPI({
              firstName: firstNameField,
              lastname: lastNameField,
              mobile: phoneField,
              mobileCodeId: phoneCodeIdField,
              cityId: cityId,
            });
          Loader.modal(false);
          if (statusResponse) {
            if (fileDetail != null) {
              uploadLogoProper();
            } else {
              showAlertMessage();
            }
          } else {
            ShowToast.error(AppTranslation.text("something_went_wrong"));
          }
        } else {
          if (fileDetail != null) {
            uploadLogoProper();
          } else {
            showAlertMessage();
          }
        }
      }
    } else {
      setAllowEdit(true);
    }
  };

  const uploadLogoProper = async () => {
    if (fileDetail != null) {
      Loader.modal();
      const uploadStatus: boolean =
        await ImagePickerPreviewProvider.uploadImage({
          fileID: "",
          flag: "1",
          file: fileDetail,
        });
      Loader.modal(false);

      if (uploadStatus) {
        showAlertMessage();
      } else {
        ShowToast.error(AppTranslation.text("something_went_wrong"));
      }
    }
  };

  const showAlertMessage = () => {
    AlertMesage({
      title: "profile_detail_updated_successfully",
      onClick: () => {
        SettingsPersonalProfileProvider.navigate(appRoutes.routeNameSettings);
      },
    });
  };

  const applyCity = () => {
    if (geoDispatch != null && geoDispatch?.city != null) {
      setCityIdField(geoDispatch?.city?.city_id! ?? "0");
      setCityNameField(textCase(geoDispatch?.city?.city_name!));
    }
  };

  useEffect(() => {
    if (countryCodeDetail != null) {
      setPhoneCodeField(countryCodeDetail?.country_phonecode!);
      setPhoneCodeIdField(countryCodeDetail?.country_id!);
    }
  }, [countryCodeDetail]);

  useEffect(() => {
    applyCity();
    // eslint-disable-next-line
  }, [geoRefreshDispatch]);

  return (
    <SettingsPersonalProfileContext.Provider
      value={{
        profileDetail,
        firstNameField,
        lastNameField,
        emailField,
        phoneField,
        phoneCodeField,
        phoneCodeIdField,
        countryId,
        countryName,
        stateId,
        stateName,
        cityId,
        cityName,
        areaId,
        areaName,

        getProfileInfo,
        submitDetail,

        profileErrors,
        profileValidate,

        handleOnChange,
        allowEdit,

        updateFileString,
      }}
    >
      {children}
    </SettingsPersonalProfileContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const SettingsPersonalProfileContextImport = () => {
  const context = useContext(SettingsPersonalProfileContext);
  if (!context) {
    throw new Error(
      "SettingsPersonalProfileContextImport must be used within a SettingsPersonalProfileContextProvider"
    );
  }
  return context;
};
